import { useState, useEffect } from "react";
import { Github, Linkedin, Mail, ArrowDown, Download } from "lucide-react";

const WORDS = [
  "CS Graduate",
  "Backend Developer",
  "Aspiring Software Engineer",
  "React Developer",
];

export default function Hero() {
  const [text, setText] = useState("");
  const [wIdx, setWIdx] = useState(0);
  const [cIdx, setCIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [mounted, setMounted] = useState(false);

  /* mount fade-in */
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  /* typewriter */
  useEffect(() => {
    const word = WORDS[wIdx];
    let delay;

    if (!deleting && cIdx < word.length) {
      delay = setTimeout(() => {
        setText(word.slice(0, cIdx + 1));
        setCIdx((c) => c + 1);
      }, 75);
    } else if (!deleting && cIdx === word.length) {
      delay = setTimeout(() => setDeleting(true), 1900);
    } else if (deleting && cIdx > 0) {
      delay = setTimeout(() => {
        setText(word.slice(0, cIdx - 1));
        setCIdx((c) => c - 1);
      }, 38);
    } else {
      setDeleting(false);
      setWIdx((i) => (i + 1) % WORDS.length);
    }

    return () => clearTimeout(delay);
  }, [cIdx, deleting, wIdx]);

  const scrollTo = (id) =>
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  const fade = (delay) =>
    `transition-all duration-700 ${
      mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0a0a0a]" />
      <div className="gradient-mesh" />
      <div className="grid-lines" />

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full bg-indigo-500/5 blur-3xl animate-[float_8s_ease-in-out_infinite]" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-cyan-500/4 blur-3xl animate-[float_11s_ease-in-out_infinite_2s]" />
      <div className="absolute top-2/3 left-2/3 w-48 h-48 rounded-full bg-green-500/4 blur-2xl animate-[float_7s_ease-in-out_infinite_1s]" />

      {/* Dot particles */}
      {Array.from({ length: 18 }, (_, i) => (
        <div
          key={i}
          className="absolute w-0.5 h-0.5 rounded-full bg-indigo-400/30"
          style={{
            left: `${10 + ((i * 37) % 80)}%`,
            top: `${10 + ((i * 53) % 80)}%`,
            animationDuration: `${5 + (i % 5)}s`,
            animationDelay: `${(i % 4) * 0.8}s`,
            animation: `float ${5 + (i % 5)}s ease-in-out ${
              (i % 4) * 0.8
            }s infinite`,
          }}
        />
      ))}

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Status badge */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-green-500/30 bg-green-500/10 mb-8 transition-all duration-700 ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{ transitionDelay: "80ms" }}
        >
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-green-400 text-sm font-mono">
            Open to junior &amp; entry-level roles
          </span>
        </div>

        {/* Name */}
        <h1
          className={`text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight mb-4 ${fade()}`}
          style={{ transitionDelay: "180ms" }}
        >
          <span className="text-[#f1f5f9]">Khadim </span>
          <span className="gradient-text">Hussain</span>
        </h1>

        {/* Typewriter */}
        <div
          className={`h-11 flex items-center justify-center mb-6 ${fade()}`}
          style={{ transitionDelay: "320ms" }}
        >
          <span className="font-mono text-xl sm:text-2xl text-cyan-400">
            {text}
            <span className="animate-[blink_1s_step-end_infinite] text-indigo-400">
              |
            </span>
          </span>
        </div>

        {/* Tagline */}
        <p
          className={`text-[#94a3b8] text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed ${fade()}`}
          style={{ transitionDelay: "460ms" }}
        >
          I build interactive web apps and I'm looking for my first engineering
          role.
          <br className="hidden sm:block" />
          <span className="font-mono text-sm text-indigo-400/70">
            {" "}
            // fresh grad, fast learner, open to learning in public
          </span>
        </p>

        {/* CTAs */}
        <div
          className={`flex flex-wrap items-center justify-center gap-4 mb-12 ${fade()}`}
          style={{ transitionDelay: "580ms" }}
        >
          <button
            onClick={() => scrollTo("projects")}
            className="group px-7 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-500/30"
          >
            View Projects
            <span className="inline-block ml-1.5 group-hover:translate-x-1 transition-transform duration-200">
              →
            </span>
          </button>
          <a
            href="/assets/resume.pdf"
            download
            className="flex items-center gap-2 px-7 py-3.5 rounded-xl border border-[#1e293b] bg-[#111111]/80 hover:border-indigo-500/50 hover:bg-indigo-500/10 text-[#f1f5f9] font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5"
          >
            <Download size={15} />
            Download Resume
          </a>
        </div>

        {/* Socials */}
        <div
          className={`flex items-center justify-center gap-4 ${fade()}`}
          style={{ transitionDelay: "700ms" }}
        >
          {[
            {
              href: "https://github.com/khadim-hussain-j",
              Icon: Github,
              label: "GitHub",
            },
            {
              href: "https://linkedin.com/in/khadim-hussainj",
              Icon: Linkedin,
              label: "LinkedIn",
            },
            {
              href: "mailto:khadimhussainj14@gmail.com",
              Icon: Mail,
              label: "Email",
            },
          ].map(({ href, Icon, label }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith("mailto") ? undefined : "_blank"}
              rel="noopener noreferrer"
              aria-label={label}
              className="p-3 rounded-xl border border-[#1e293b] bg-[#111111]/80 text-[#94a3b8] hover:text-indigo-400 hover:border-indigo-500/50 hover:bg-indigo-500/10 hover:-translate-y-1 transition-all duration-200"
            >
              <Icon size={18} />
            </a>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <button
        onClick={() => scrollTo("about")}
        aria-label="Scroll to about section"
        className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-[#94a3b8]/60 hover:text-indigo-400 transition-all duration-700 ${
          mounted ? "opacity-100" : "opacity-0"
        }`}
        style={{ transitionDelay: "900ms" }}
      >
        <span className="font-mono text-xs">scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </button>
    </section>
  );
}
